// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { navigate, RouteComponentProps } from "@reach/router";
import { Button, Divider, Input, message, Table, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useStore } from "effector-react";
import { columns } from "./NewsThemeListColumns";
import { PlusCircleOutlined } from "@ant-design/icons";
import {
  AuthentifiedUser,
  NewsThemeInfoResponseDTO
} from "../../../../api/backend-openapi";
import {
  requestDecreaseOrderNewsTheme,
  requestDeleteNewsTheme,
  requestGetNewsThemes,
  requestIncreaseOrderNewsTheme,
  requestWorkflowNewsTheme
} from "../../../../state/newsTheme/NewsThemeEffects";
import { NewsThemeStore } from "../../../../state/newsTheme/NewsThemeStore";
import {
  deleteNewsTheme,
  setNewsThemes,
  updateNewsTheme
} from "../../../../state/newsTheme/NewsThemeEvents";
import PageTitle from "../../../components/PageTitle";
import PageBody from "../../../components/PageBody";
import PageHeader from "../../../components/PageHeader";
import { SpinIcon } from "../../../components/SpinIcon";
import { SearchOutlined } from "@ant-design/icons/lib";
import Highlighter from "react-highlight-words";
import { authentifiedUserStore } from "../../../../state/auth/AuthStore";
import { NboAction } from "../../../types/AuthTypes";
import { BranchesStore } from "../../../../state/branches/BranchesStore";
import { displayError } from "../../../../utils/displayError";

const customTooltipDelay = 0.05;

const NewsThemesScreen: React.FunctionComponent<RouteComponentProps> = () => {
  const { t } = useTranslation();

  const newsThemeStore: NewsThemeInfoResponseDTO[] | null = useStore(
    NewsThemeStore
  );
  const authentifiedUser: AuthentifiedUser | null = useStore(
    authentifiedUserStore
  );
  const canCreate: boolean =
    authentifiedUser?.rights.nboNewsTheme?.includes(NboAction.nboCreate) ||
    false;
  const canDelete: boolean =
    authentifiedUser?.rights.nboNewsTheme?.includes(NboAction.nboDelete) ||
    false;
  const canValidate: boolean =
    authentifiedUser?.rights.nboNewsTheme?.includes(NboAction.nboValidate) ||
    false;

  const [searchText, setSearchText] = useState<string>("");

  const [dataHasLoaded, setDataHasLoaded] = useState<boolean>(false);

  const branchList = useStore(BranchesStore);

  useEffect(() => {
    requestGetNewsThemes();
  }, []);

  useEffect(() => {
    return requestGetNewsThemes.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setNewsThemes(result.data);
        setDataHasLoaded(true);
      } else {
        displayError("news_themes.load_failed");
      }
    });
  });

  useEffect(() => {
    return requestIncreaseOrderNewsTheme.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setNewsThemes(result.data);
      } else {
        displayError("news_themes.load_failed");
      }
    });
  });

  useEffect(() => {
    return requestDecreaseOrderNewsTheme.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setNewsThemes(result.data);
      } else {
        displayError("news_themes.load_failed");
      }
    });
  });

  useEffect(() => {
    return requestDeleteNewsTheme.done.watch(({ params, result }) => {
      if (result.ok) {
        deleteNewsTheme(params.id);
        message.success(t("news_themes.global_buttons.delete.success"));
      } else {
        displayError("news_themes.global_buttons.delete.error");
      }
    });
  });

  useEffect(() => {
    return requestWorkflowNewsTheme.done.watch(({ result }) => {
      if (result.ok && result.data) {
        updateNewsTheme(result.data);
        message.success(t("edit_news_theme.success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  const search = (selectedKeys: string, confirm: any) => {
    confirm();
    setSearchText(
      selectedKeys[0] ? selectedKeys[0].toString().toLowerCase() : ""
    );
  };

  const getColumnSearchProps = () => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: any) => (
      <div className="d-flex align-items-stretch justify-content-center p-2">
        <Input
          placeholder={t("news_themes.search_placeholder")}
          value={selectedKeys[0]}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => search(selectedKeys, confirm)}
          className="flex-grow-1"
        />
        <Button
          type="primary"
          onClick={() => search(selectedKeys, confirm)}
          icon={<SearchOutlined />}
          size="large"
          className="btn-search"
        />
      </div>
    ),
    filterIcon: (filtered: any) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value: any, record: any) =>
      record["name"]
        ? record["name"]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    render: (record: NewsThemeInfoResponseDTO) => (
      <Tooltip
        mouseEnterDelay={customTooltipDelay}
        mouseLeaveDelay={customTooltipDelay}
        placement="top"
        title={record.name}
      >
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={record.name ? record.name : ""}
          unhighlightStyle={{ color: record.color, fontWeight: "bold" }}
        />
      </Tooltip>
    )
  });

  return (
    <>
      <PageHeader>
        <PageTitle title={t("news_themes.title")} />
      </PageHeader>
      <PageBody className="list-body">
        {
          <>
            <div className="d-flex flex-row justify-content-end align-items-stretch flex-wrap">
              <div className="flex-grow-1 d-flex align-items-center">
                <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
                  <h3 className="text-secondary mb-0">
                    {t("news_themes.list_title")}
                  </h3>
                </Divider>
              </div>
              {canCreate && (
                <div className="d-flex align-items-center divider-sm-transparent">
                  <Divider orientation="right" className="mt-2 mt-sm-3 mb-3">
                    <Button
                      type="default"
                      className="btn-secondary"
                      onClick={() => navigate("/newsThemes/add")}
                    >
                      <PlusCircleOutlined />
                      {t("news_themes.global_buttons.add")}
                    </Button>
                  </Divider>
                </div>
              )}
            </div>
            <Table
              locale={{ emptyText: dataHasLoaded ? "" : " " }}
              loading={{ spinning: !dataHasLoaded, indicator: SpinIcon }}
              columns={columns(
                getColumnSearchProps,
                canValidate,
                canDelete,
                searchText === "" ? newsThemeStore : null,
                branchList
              )}
              dataSource={
                searchText === ""
                  ? newsThemeStore
                  : newsThemeStore.filter(
                      (newsTheme: NewsThemeInfoResponseDTO) => {
                        return newsTheme.name
                          .toLowerCase()
                          .includes(searchText);
                      }
                    )
              }
              showSorterTooltip={false}
              rowKey={record => record.id}
              rowClassName="cursor-pointer"
              onRow={record => {
                return {
                  onClick: () => navigate(`/newsThemes/${record.id}`)
                };
              }}
              pagination={{
                total: newsThemeStore.length,
                pageSize: newsThemeStore.length,
                hideOnSinglePage: true
              }}
            />
          </>
        }
      </PageBody>
    </>
  );
};

export default NewsThemesScreen;
